import {
  DOCUMENT_QUESTION_TYPES,
  build_document_record,
  extract_response_items,
  extract_rules_response_items,
  normalize_form,
  normalize_rule,
  normalize_transition_rule,
  parse_response_data,
  get_status_text,
  get_rule_statuses,
  plural_suffix,
  get_transition_condition
} from './document_helpers';
import fetch_methods from './manage_documents_fetch_methods';
import assignment_methods from './manage_documents_assignment_methods';

function get_section_sub_sections(section) {
  return section?.sub_sections || [];
}

function get_sub_section_questions(sub_section) {
  return sub_section?.questions || [];
}

function build_document_from_question(context, section, sub_section, question) {
  return context.build_document_record(question, section, sub_section);
}

function first_defined_value(...values) {
  const value = values.find((item) => item !== undefined && item !== null && item !== '');
  if (value !== undefined) return value;
  return values.at(-1);
}

function extract_section_document_questions(context, section) {
  return get_section_sub_sections(section).flatMap((sub_section) =>
    extract_sub_section_document_questions(context, section, sub_section)
  );
}

function extract_sub_section_document_questions(context, section, sub_section) {
  return get_sub_section_questions(sub_section)
    .filter((question) => context.is_document_question(question))
    .map((question) => build_document_from_question(context, section, sub_section, question));
}

function build_assigned_document_question(context, form, main_section, sub_section, question) {
  const question_id = first_defined_value(question.id, question.section_question_id);
  const section_question_id = Number(first_defined_value(question.id, question.section_question_id, 0));
  const section_name = first_defined_value(sub_section.sub_section_name, main_section.main_section_name);
  return {
    id: question_id,
    section_question_id,
    name: first_defined_value(question.question_text, 'Document field'),
    type: first_defined_value(context.get_question_type(question), 'File'),
    status: question.is_mandatory ? 'Required' : 'Optional',
    form_name: first_defined_value(form.form_name),
    section_name,
    requires_verification: Boolean(question.requires_verification),
    compliance_field_id: first_defined_value(question.compliance_field_id, null)
  };
}

function extract_assigned_sub_section_document_questions(context, form, main_section, sub_section) {
  return get_sub_section_questions(sub_section)
    .filter((question) => context.is_document_question(question))
    .map((question) => build_assigned_document_question(context, form, main_section, sub_section, question));
}

function extract_assigned_main_section_document_questions(context, form, main_section) {
  return get_section_sub_sections(main_section).flatMap((sub_section) =>
    extract_assigned_sub_section_document_questions(context, form, main_section, sub_section)
  );
}

function extract_assigned_form_document_questions(context, form) {
  return (form?.main_sections || []).flatMap((main_section) =>
    extract_assigned_main_section_document_questions(context, form, main_section)
  );
}

export default {
    ...fetch_methods,
    ...assignment_methods,
    parse_response_data,
    extract_response_items,
    extract_rules_response_items,
    normalize_form,
    normalize_rule,
    normalize_transition_rule,
    format_rule_duration(value) {
      if (!value) return 'Optional';
      const text = String(value);
      const day_match = text.match(/^(\d+)\s/);
      if (day_match) {
        const days = Number(day_match[1]);
        return `${days} day${days === 1 ? '' : 's'}`;
      }
      return text;
    },
    get_rule_trigger_status_label(rule) {
      const statuses = get_rule_statuses(rule);
      if (statuses.length) return statuses.map(get_status_text).join(', ');
      return rule?.trigger_status_name || 'No trigger status';
    },
    get_transition_rule_document_ids(transition_rule) {
      const rules = Array.isArray(transition_rule?.condition?.rules)
        ? transition_rule.condition.rules
        : [];
      const document_rule = rules.find((rule) => rule.action_type === 'document_requirement');
      return (document_rule?.selected_questions || []).map((id) => String(id)).filter(Boolean);
    },
    get_transition_rule_type_label(transition_rule) {
      return transition_rule?.transition_type?.name || transition_rule?.transition_type_name || 'Transition';
    },
    get_transition_rule_status_label(transition_rule) {
      return transition_rule?.is_active === false ? 'Inactive' : 'Active';
    },
    get_transition_rule_condition_label(transition_rule) {
      const condition = get_transition_condition(transition_rule);
      return `${condition.operator} logic - ${condition.rule_count} condition${plural_suffix(condition.rule_count)}`;
    },
    get_form_type_name(form) {
      return String(form?.form_type_name || '').trim();
    },
    is_document_form(form) {
      const normalized = this.get_form_type_name(form).toLowerCase();
      return ['document', 'form document', 'document form'].includes(normalized)
        || normalized.includes('document');
    },
    get_question_type(question) {
      const question_type = question?.question_type?.question_type
        || question?.question_type?.type
        || question?.question_type
        || '';

      return String(question_type).trim();
    },
    is_document_question(question) {
      const normalized_type = this.get_question_type(question).toLowerCase();
      return DOCUMENT_QUESTION_TYPES.includes(normalized_type);
    },
    build_document_record(question, section, sub_section) {
      const form_name = this.selected_form?.form_name || '';
      return build_document_record(
        question,
        section,
        sub_section,
        this.selected_form_id,
        form_name,
        this.get_question_type(question)
      );
    },
    extract_document_questions(form_structure) {
      const sections = Array.isArray(form_structure) ? form_structure : [];
      return sections.flatMap((section) => extract_section_document_questions(this, section));
    },
    flatten_assigned_document_questions(assigned_tree) {
      const forms = Array.isArray(assigned_tree) ? assigned_tree : [];
      return forms.flatMap((form) => extract_assigned_form_document_questions(this, form));
    },
    open_form_documents(form) {
      this.selected_form_id = String(form?.id || '');
    },
    clear_selected_form() {
      this.selected_form_id = '';
    },
    toggle_document_selection(document) {
      const section_question_id = String(document?.section_question_id || '');
      if (!section_question_id) {
        return;
      }

      const is_selected = this.selected_document_ids.includes(section_question_id);
      this.selected_document_ids = is_selected
        ? this.selected_document_ids.filter((id) => id !== section_question_id)
        : [...this.selected_document_ids, section_question_id];
    },
    toggle_select_all_documents() {
      if (this.all_visible_documents_selected) {
        const visible_ids = new Set(this.paginated_documents.map((document) => String(document.section_question_id)));
        this.selected_document_ids = this.selected_document_ids.filter((id) => !visible_ids.has(id));
        return;
      }

      const merged = new Set(this.selected_document_ids);
      this.paginated_documents.forEach((document) => {
        merged.add(String(document.section_question_id));
      });
      this.selected_document_ids = Array.from(merged);
    },
    preview_document(document) {
      this.selected_document = document || null;
    }
};

